import {CommonMethods} from '@PosterWhiteboard/common-methods';
import type {Poster} from '@PosterWhiteboard/poster/poster.class';
import type {ImageBackgroundObject} from '@PosterWhiteboard/page/background/image-background.class';
import type {PosterObject, ResizeDimensions} from '@PosterWhiteboard/poster/poster.types';
import type {PosterTypeDimensions, PosterTypeObject} from '@PosterWhiteboard/poster-type/poster-type.types';
import type {ItemObject} from '@PosterWhiteboard/items/item/item.types';
import type {PageObject} from '@PosterWhiteboard/page/page.types';
import {BackgroundTypeName} from '@PosterWhiteboard/page/background/background.types';

interface DimesionsAndCoordinates {
  width: number;
  height: number;
  x: number;
  y: number;
}

export class ResizePoster extends CommonMethods {
  public poster: Poster;

  constructor(poster: Poster) {
    super();
    this.poster = poster;
  }

  public getResizedPosterObjectForTemplate(posterType: PosterTypeObject): PosterObject {
    const updatedPoster = this.poster.toObject();

    const currentWidth = updatedPoster.width;
    const currentHeight = updatedPoster.height;

    if (!(currentWidth === posterType.width && currentHeight === posterType.height)) {
      for (const pageKey of Object.keys(updatedPoster.pages.pagesHashMap)) {
        const page = updatedPoster.pages.pagesHashMap[pageKey];
        for (const [, itemObject] of Object.entries(updatedPoster.pages.pagesHashMap[pageKey].items.itemsHashMap)) {
          const resizeDimensions = this.getDimensionsToResizePageItemsForTemplate(updatedPoster.width, updatedPoster.height, itemObject, {
            width: posterType.width,
            height: posterType.height,
          });
          itemObject.left += resizeDimensions.left;
          itemObject.top += resizeDimensions.top;
          itemObject.scaleX *= resizeDimensions.scaleFactor;
          itemObject.scaleY *= resizeDimensions.scaleFactor;
        }

        if (page.background.details.type === BackgroundTypeName.IMAGE) {
          const imageBackground = page.background.details;
          const bgDimsAndCoordsData = getBgDimsAndCoordsData(updatedPoster, posterType, imageBackground);

          if (!imageBackground.cropData) {
            imageBackground.cropData = {
              x: 0,
              y: 0,
              width: 0,
              height: 0,
              imageWidth: 0,
              imageHeight: 0,
              cropped: true,
            };
          }
          imageBackground.cropData.x = bgDimsAndCoordsData.x;
          imageBackground.cropData.y = bgDimsAndCoordsData.y;
          imageBackground.cropData.width = bgDimsAndCoordsData.width;
          imageBackground.cropData.height = bgDimsAndCoordsData.height;
        }
      }

      updatedPoster.width = posterType.width;
      updatedPoster.height = posterType.height;
    }
    updatedPoster.type = posterType;
    return updatedPoster;
  }

  public getDimensionsToResizePageItemsForTemplate(width: number, height: number, item: ItemObject, posterTypeDimensions: PosterTypeDimensions): ResizeDimensions {
    const currentAspectRatio = width / height;
    const params = getParametersForCoverResizing(posterTypeDimensions.width, posterTypeDimensions.height, currentAspectRatio);
    const scaleFactor = params.height / height;
    const resizeXAdjust = width - params.width;
    const resizeYAdjust = height - params.height;

    return {
      left: Math.round(params.x - (item.left / width) * resizeXAdjust),
      top: Math.round(params.y - (item.top / height) * resizeYAdjust),
      scaleFactor,
    };
  }

  public getDimensionsToResizePageItemsToTargetType(width: number, height: number, item: ItemObject, posterTypeDimensions: PosterTypeDimensions): ResizeDimensions {
    const currentWidth = width;
    const currentHeight = height;
    const currentAspectRatio = currentWidth / currentHeight;

    const params = getParametersForResizing(posterTypeDimensions.width, posterTypeDimensions.height, currentAspectRatio);
    const scaleFactor = params.height / currentHeight;
    const resizeXAdjust = currentWidth - params.width;
    const resizeYAdjust = currentHeight - params.height;

    return {
      left: Math.floor(params.x - (item.left / currentWidth) * resizeXAdjust),
      top: Math.floor(params.y - (item.top / currentHeight) * resizeYAdjust),
      scaleFactor,
    };
  }

  public getResizedPosterObjectForPosterType(posterType: PosterTypeObject): PosterObject {
    const updatedPoster = this.poster.toObject();

    const currentWidth = updatedPoster.width;
    const currentHeight = updatedPoster.height;

    if (!(currentWidth === posterType.width && currentHeight === posterType.height)) {
      for (const pageKey of Object.keys(updatedPoster.pages.pagesHashMap)) {
        const page = updatedPoster.pages.pagesHashMap[pageKey];
        for (const [, itemObject] of Object.entries(updatedPoster.pages.pagesHashMap[pageKey].items.itemsHashMap)) {
          const resizeDimensions = this.getDimensionsToResizePageItemsToTargetType(updatedPoster.width, updatedPoster.height, itemObject, {
            width: posterType.width,
            height: posterType.height,
          });
          itemObject.left += resizeDimensions.left;
          itemObject.top += resizeDimensions.top;
          itemObject.scaleX *= resizeDimensions.scaleFactor;
          itemObject.scaleY *= resizeDimensions.scaleFactor;
        }

        if (page.background.details.type === BackgroundTypeName.IMAGE) {
          const imageBackground = page.background.details;
          const bgDimsAndCoordsData = getBgDimsAndCoordsData(updatedPoster, posterType, imageBackground);

          if (!imageBackground.cropData) {
            imageBackground.cropData = {
              x: 0,
              y: 0,
              width: 0,
              height: 0,
              imageWidth: 0,
              imageHeight: 0,
              cropped: true,
            };
          }
          imageBackground.cropData.x = bgDimsAndCoordsData.x;
          imageBackground.cropData.y = bgDimsAndCoordsData.y;
          imageBackground.cropData.width = bgDimsAndCoordsData.width;
          imageBackground.cropData.height = bgDimsAndCoordsData.height;
        }
      }

      updatedPoster.width = posterType.width;
      updatedPoster.height = posterType.height;
    }
    updatedPoster.type = posterType;
    return updatedPoster;
  }

  public resizePagesForAddedDesign(pagesHashMap: Record<string, PageObject>, fromWidth: number, fromHeight: number, toWidth: number, toHeight: number): void {
    if (fromWidth === toWidth && fromHeight === toHeight) {
      return;
    }
    const sourceDimensions = {width: fromWidth, height: fromHeight} as PosterObject;
    const targetDimensions = {width: toWidth, height: toHeight} as PosterTypeObject;

    for (const pageKey of Object.keys(pagesHashMap)) {
      const page = pagesHashMap[pageKey];
      for (const [, itemObject] of Object.entries(page.items.itemsHashMap)) {
        const resizeDimensions = this.getDimensionsToResizePageItemsToTargetType(fromWidth, fromHeight, itemObject, {width: toWidth, height: toHeight});
        itemObject.left += resizeDimensions.left;
        itemObject.top += resizeDimensions.top;
        itemObject.scaleX *= resizeDimensions.scaleFactor;
        itemObject.scaleY *= resizeDimensions.scaleFactor;
      }

      if (page.background.details.type === BackgroundTypeName.IMAGE) {
        const imageBackground = page.background.details;
        const bgDimsAndCoordsData = getBgDimsAndCoordsData(sourceDimensions, targetDimensions, imageBackground);

        if (!imageBackground.cropData) {
          imageBackground.cropData = {
            x: 0,
            y: 0,
            width: 0,
            height: 0,
            imageWidth: 0,
            imageHeight: 0,
            cropped: true,
          };
        }
        imageBackground.cropData.x = bgDimsAndCoordsData.x;
        imageBackground.cropData.y = bgDimsAndCoordsData.y;
        imageBackground.cropData.width = bgDimsAndCoordsData.width;
        imageBackground.cropData.height = bgDimsAndCoordsData.height;
      }
    }
  }
}

const getParametersForCoverResizing = (targetWidth: number, targetHeight: number, currentAspectRatio: number): DimesionsAndCoordinates => {
  let newHeight = targetHeight;
  let newWidth = targetHeight * currentAspectRatio;

  if (newWidth < targetWidth) {
    newWidth = targetWidth;
    newHeight = targetWidth / currentAspectRatio;
  }

  return {height: newHeight, width: newWidth, x: (targetWidth - newWidth) / 2, y: (targetHeight - newHeight) / 2};
};

const getParametersForResizing = (targetWidth: number, targetHeight: number, currentAspectRatio: number): DimesionsAndCoordinates => {
  let newHeight;
  let newWidth;

  newHeight = targetHeight;
  newWidth = targetHeight * currentAspectRatio;

  if (newWidth > targetWidth) {
    newWidth = targetWidth;
    newHeight = targetWidth / currentAspectRatio;
  }

  return {height: newHeight, width: newWidth, x: (targetWidth - newWidth) / 2, y: (targetHeight - newHeight) / 2};
};

const coordinatesAndDimensionsForBackground = (background: ImageBackgroundObject, targetAspectRatio: number): DimesionsAndCoordinates => {
  const backgroundRatio = background.cropData.width / background.cropData.height;
  const bgData = {} as DimesionsAndCoordinates;
  if (backgroundRatio < targetAspectRatio) {
    bgData.x = background.cropData.x;
    bgData.width = background.cropData.width;
    bgData.height = Math.floor(bgData.width / targetAspectRatio);
    bgData.y = background.cropData.y + (background.cropData.height - bgData.height) / 2;
  } else {
    bgData.y = background.cropData.y;
    bgData.height = background.cropData.height;
    bgData.width = Math.floor(bgData.height * targetAspectRatio);
    bgData.x = background.cropData.x + (background.cropData.width - bgData.width) / 2;
  }
  return bgData;
};

const getBgDimsAndCoordsData = (poster: PosterObject, posterType: PosterTypeObject, imageBackground: ImageBackgroundObject): DimesionsAndCoordinates => {
  const currentAspectRatio = poster.width / poster.height;
  const targetAspectRatio = posterType.width / posterType.height;

  let bgDimsAndCoordsData;
  if (currentAspectRatio < targetAspectRatio || (posterType.width === posterType.height && poster.width < poster.height)) {
    if (imageBackground.cropData) {
      bgDimsAndCoordsData = coordinatesAndDimensionsForBackground(imageBackground, targetAspectRatio);
    } else {
      bgDimsAndCoordsData = {
        width: imageBackground.width,
        height: Math.floor(imageBackground.width / targetAspectRatio),
        x: 0,
        y: (imageBackground.height - Math.floor(imageBackground.width / targetAspectRatio)) / 2,
      };
    }
  } else if (imageBackground.cropData) {
    bgDimsAndCoordsData = coordinatesAndDimensionsForBackground(imageBackground, targetAspectRatio);
  } else {
    bgDimsAndCoordsData = {
      width: Math.floor(imageBackground.height * targetAspectRatio),
      height: imageBackground.height,
      y: 0,
      x: (imageBackground.width - Math.floor(imageBackground.height * targetAspectRatio)) / 2,
    };
  }
  return bgDimsAndCoordsData;
};
